import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getAuthHeader } from "../../api/authToken.js";
import "../../styles/App.css";
import { analyzeRooms } from "../../api/dss.js";
import { GENDER_LABELS, SKIPPED_ROOM_REASONS, groupSkipped } from "../../decision/allocationLabels.js";

// «Вільні кімнати»: стан житлового фонду по блоках. Стать блоку виводиться
// з мешканців (порожній блок бере стать першого поселеного), тож тут видно,
// де ще можна поселити чоловіків, де жінок, а які блоки порожні. Блок, де в
// мешканця не вказано стать, має стать «невідома» (червоним), а кімната з
// такими мешканцями -- позначку «N без статі/курсу». Номер кімнати веде на її
// картку (/allocation/rooms/:id).
const FreeRoomsPage = () => {
  const API = process.env.REACT_APP_API_BASE;
  const [dormitories, setDormitories] = useState([]);
  const [dormitoryId, setDormitoryId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [onlyFree, setOnlyFree] = useState(false);

  useEffect(() => {
    const fetchDormitories = async () => {
      try {
        const res = await axios.get(`${API}/api/admin-rooms/admin-dormitories`, {
          headers: { Authorization: getAuthHeader() },
        });
        setDormitories(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchDormitories();
  }, [API]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await analyzeRooms({ dormitoryId });
        if (!cancelled) setAnalysis(result);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setAnalysis(null);
          setError(
            err.status === 503
              ? "Модуль підтримки рішень зараз недоступний, спробуйте пізніше."
              : "Не вдалося отримати стан кімнат."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [dormitoryId]);

  const totals = analysis?.totals;
  const blocks = (analysis?.blocks || []).filter((b) => !onlyFree || b.freeBeds > 0);
  const skipped = groupSkipped(analysis?.skipped, SKIPPED_ROOM_REASONS);

  return (
    <div className="page-container">
      <h3 className="text-header">Вільні кімнати</h3>

      <div className="header-content">
        <select
          className="header-content-input"
          aria-label="Гуртожиток"
          value={dormitoryId}
          onChange={(e) => setDormitoryId(e.target.value)}
        >
          <option value="">Усі гуртожитки</option>
          {dormitories.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <label className="text-header">
          <input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} /> Лише блоки з
          вільними місцями
        </label>
      </div>

      {loading && <p className="text-header">Завантаження...</p>}
      {error && (
        <p className="text-header" role="alert" style={{ color: "var(--full-busy)" }}>
          {error}
        </p>
      )}

      {totals && (
        <>
          <div className="input-component" data-testid="free-totals">
            <div className="form-container">
              <p className="input-block text-header">
                <strong>Вільних місць для чоловіків:</strong> {totals.freeBedsMale}
              </p>
              <p className="input-block text-header">
                <strong>Вільних місць для жінок:</strong> {totals.freeBedsFemale}
              </p>
              <p className="input-block text-header">
                <strong>Вільних місць у порожніх блоках:</strong> {totals.freeBedsEmptyBlocks}
              </p>
              <p className="input-block text-header">
                <strong>Вільних місць у блоках з невідомою статтю:</strong>{" "}
                <span className={totals.freeBedsUnknown > 0 ? "missing-value" : undefined}>
                  {totals.freeBedsUnknown ?? 0}
                </span>
              </p>
              <p className="input-block text-header">
                <strong>Усього вільних місць:</strong> {totals.freeBedsTotal}
              </p>
            </div>
            <div className="form-container">
              <p className="input-block text-header">
                <strong>Блоків:</strong> чоловічих {totals.blocksMale}, жіночих {totals.blocksFemale}, порожніх{" "}
                {totals.blocksEmpty}, з невідомою статтю {totals.blocksUnknown ?? 0}
              </p>
              <p className="input-block text-header">
                <strong>Кімнат із поєднанням курсів 1 і 4+:</strong> {(totals.roomsWithCourseMix || []).length}
              </p>
            </div>
            {skipped.length > 0 && (
              <p className="text-header" data-testid="free-skipped">
                Не враховано кімнат: {skipped.reduce((n, s) => n + s.count, 0)} (
                {skipped.map((s) => `${s.label}: ${s.count}`).join("; ")}).
              </p>
            )}
          </div>

          <table className="table" data-testid="free-blocks">
            <thead>
              <tr>
                {!dormitoryId && <th className="table-header">Гуртожиток</th>}
                <th className="table-header">Блок</th>
                <th className="table-header">Стать блоку</th>
                <th className="table-header">Вільні місця</th>
                <th className="table-header">Кімнати (вільно з місткості)</th>
              </tr>
            </thead>
            <tbody>
              {blocks.length > 0 ? (
                blocks.map((b) => (
                  <tr key={`${b.dormitoryId ?? ""}-${b.block}`}>
                    {!dormitoryId && <td className="table-content">{b.dormitoryName || "—"}</td>}
                    <td className="table-content">{b.block}</td>
                    <td className="table-content">
                      {b.gender === "unknown" ? (
                        <span className="missing-value" title="У блоці є мешканці без вказаної статі">
                          {GENDER_LABELS.unknown}
                        </span>
                      ) : (
                        GENDER_LABELS[b.gender] || b.gender
                      )}
                      {b.genderConflict && (
                        <span title="У блоці є мешканці різної статі" style={{ color: "var(--full-busy)" }}>
                          {" "}
                          ⚠ змішана стать
                        </span>
                      )}
                    </td>
                    <td className="table-content">{b.freeBeds}</td>
                    <td className="table-content">
                      {(b.rooms || []).map((r) => (
                        <div key={r.id}>
                          <Link to={`/allocation/rooms/${r.id}`} title={`Картка кімнати ${r.number}`}>
                            {r.number}
                          </Link>
                          : {r.freeBeds} з {r.capacity}
                          {r.courseMix && (
                            <span title="Разом курс 1 і курс 4+" style={{ color: "var(--full-busy)" }}>
                              {" "}
                              ⚠ курси 1 і 4+
                            </span>
                          )}
                          {r.overfilled && <span style={{ color: "var(--full-busy)" }}> ⚠ переповнена</span>}
                          {r.unknownResidents > 0 && (
                            <span className="missing-value" title="Мешканці, у яких не вказано стать чи курс">
                              {" "}
                              ⚠ {r.unknownResidents} без статі/курсу
                            </span>
                          )}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="table-content" colSpan={dormitoryId ? 4 : 5}>
                    Немає блоків для показу.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default FreeRoomsPage;
